import { ReactElement, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heading } from '../../../components/Heading';
import { Paragraph } from '../../../components/Paragraph';
import { Button } from '../../../components/Button/Button';
import classes from './CustomerListPage.module.css';
import { Alert } from '../../../components/Alert/Alert';
import { useGetPartnerCustomersQuery } from '../../../hooks/queries/useGetPartnerCustomersQuery';
import { GhostLoader } from '../../../components/GhostLoader/GhostLoader';
import { CustomerTable } from '../../../components/CustomerTable';
import { Routes } from '../../../enums/Routes';
import { ContainerCard } from '../../../components/ContainerCard/ContainerCard';
import { StatsCardsGrid, type StatsCardConfig } from '../../../components/StatsCard/StatsCard';
import { PersonGroupIcon, BriefcaseIcon, PhoneIcon } from '@navikt/aksel-icons';

const PAGE_TITLE = 'Mine kunder';
const ERROR_MESSAGE = 'Kunne ikke laste kunder. Prøv igjen senere.';
const ADD_CUSTOMER_BUTTON = 'Legg til ny kunde';

type AggregatedStats = {
  readonly totalTeams: number;
  readonly totalUsers: number;
  readonly totalSmsUsed: number;
};

type Customer = {
  readonly teamCount: number;
  readonly seatsUsed: number;
  readonly smsUsed: number;
};

const calculateAggregatedStats = (customers: readonly Customer[]): AggregatedStats => {
  return customers.reduce(
    (acc, customer) => ({
      totalTeams: acc.totalTeams + customer.teamCount,
      totalUsers: acc.totalUsers + customer.seatsUsed,
      totalSmsUsed: acc.totalSmsUsed + customer.smsUsed,
    }),
    { totalTeams: 0, totalUsers: 0, totalSmsUsed: 0 }
  );
};

const createStatsCards = (totalCustomers: number, stats: AggregatedStats): readonly StatsCardConfig[] => [
  {
    title: 'Totalt antall kunder',
    icon: <BriefcaseIcon />,
    value: totalCustomers,
  },
  {
    title: 'Totalt antall team',
    icon: <PersonGroupIcon />,
    value: stats.totalTeams,
    meta: 'på tvers av kunder',
  },
  {
    title: 'Totalt antall brukere',
    icon: <PersonGroupIcon />,
    value: stats.totalUsers,
    meta: 'aktive lisenser',
  },
  {
    title: 'SMS brukt',
    icon: <PhoneIcon />,
    value: stats.totalSmsUsed,
    meta: 'totalt',
    formatter: (val) => typeof val === 'number' ? val.toLocaleString('no-NO') : val,
  },
];

export const CustomerListPage = (): ReactElement => {
  const navigate = useNavigate();
  const { data, isLoading, error } = useGetPartnerCustomersQuery();

  const handleCreateCustomer = () => {
    navigate(Routes.PartnerAdminCreateCustomer);
  };

  const aggregatedStats = useMemo<AggregatedStats>(() => {
    if (!data?.customers) {
      return { totalTeams: 0, totalUsers: 0, totalSmsUsed: 0 };
    }
    return calculateAggregatedStats(data.customers);
  }, [data?.customers]);

  const statsCards = useMemo(() => {
    return createStatsCards(data?.total ?? 0, aggregatedStats);
  }, [data?.total, aggregatedStats]);

  return (
    <div className={classes.container}>
      <header className={classes.header}>
        <div className={classes.headerContent}>
          <div>
            <Heading as="h1">{PAGE_TITLE}</Heading>
            {data?.partnerInfo && (
              <Paragraph size="lg">
                {data.partnerInfo.name} - Administrer dine kunder
              </Paragraph>
            )}
          </div>
          <Button variant="primary" size="medium" onClick={handleCreateCustomer}>
            {ADD_CUSTOMER_BUTTON}
          </Button>
        </div>
      </header>

      <div className={classes.content}>
        {isLoading && <GhostLoader />}

        {error && (
          <Alert variant="danger" size="md">
            <Paragraph>{ERROR_MESSAGE}</Paragraph>
          </Alert>
        )}

        {data && !isLoading && (
          <>
            <StatsCardsGrid cards={statsCards} columns={4} />

            <ContainerCard>
              <ContainerCard.Title>Kunder</ContainerCard.Title>
              <ContainerCard.Body noPadding>
                <CustomerTable customers={data.customers} />
              </ContainerCard.Body>
            </ContainerCard>
          </>
        )}
      </div>
    </div>
  );
};
